import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import BackupRestorePanel from '../components/BackupRestorePanel';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2,
  Users,
  Store,
  TrendingUp,
  Plus,
  Search,
  Mail,
  Phone,
  Globe,
  Palette,
  Settings,
  Eye,
  Edit,
  UserPlus,
  ChevronRight,
  BarChart3,
  FileText,
  CheckCircle,
  Clock,
  Copy,
  DatabaseBackup,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformAdminPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Builders
  const [builders, setBuilders] = useState([]);
  const [selectedBuilder, setSelectedBuilder] = useState(null);
  const [showBuilderModal, setShowBuilderModal] = useState(false);
  const [builderForm, setBuilderForm] = useState({
    name: '',
    slug: '',
    email: '',
    phone: '',
    website: ''
  });
  
  // Sales Companies
  const [salesCompanies, setSalesCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    builder_id: '',
    name: '',
    slug: '',
    email: '',
    phone: ''
  });
  
  // Invite modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteType, setInviteType] = useState('builder_admin'); // builder_admin, company_admin, sales_rep
  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [inviteResult, setInviteResult] = useState(null);
  
  // All users
  const [allUsers, setAllUsers] = useState([]);
  const [userFilter, setUserFilter] = useState({ builder_id: '', sales_company_id: '', tenant_role: '' });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDashboard();
    fetchBuilders();
    fetchSalesCompanies();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/platform/dashboard`, { headers });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchBuilders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/builders/`, { headers });
      setBuilders(response.data);
    } catch (error) {
      console.error('Failed to fetch builders:', error);
    }
  };

  const fetchSalesCompanies = async (builderId = null) => {
    try {
      const url = builderId 
        ? `${API_URL}/api/builders/${builderId}/sales-companies`
        : `${API_URL}/api/sales-companies/`;
      const response = await axios.get(url, { headers });
      setSalesCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch sales companies:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (userFilter.builder_id) params.append('builder_id', userFilter.builder_id);
      if (userFilter.sales_company_id) params.append('sales_company_id', userFilter.sales_company_id);
      if (userFilter.tenant_role) params.append('tenant_role', userFilter.tenant_role);
      
      const response = await axios.get(`${API_URL}/api/platform/all-users?${params}`, { headers });
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchAllUsers();
    }
  }, [activeTab, userFilter]);

  const handleCreateBuilder = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/builders/`, {
        name: builderForm.name,
        slug: builderForm.slug || builderForm.name.toLowerCase().replace(/\s+/g, '-'),
        email: builderForm.email,
        phone: builderForm.phone,
        branding: {
          company_website: builderForm.website
        }
      }, { headers });
      
      toast.success('Builder created successfully');
      setShowBuilderModal(false);
      setBuilderForm({ name: '', slug: '', email: '', phone: '', website: '' });
      fetchBuilders();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create builder');
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/sales-companies/`, {
        builder_id: companyForm.builder_id,
        name: companyForm.name,
        slug: companyForm.slug || companyForm.name.toLowerCase().replace(/\s+/g, '-'),
        email: companyForm.email,
        phone: companyForm.phone
      }, { headers });
      
      toast.success('Sales company created successfully');
      setShowCompanyModal(false);
      setCompanyForm({ builder_id: '', name: '', slug: '', email: '', phone: '' });
      fetchSalesCompanies();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sales company');
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      let url = '';
      if (inviteType === 'builder_admin') {
        url = `${API_URL}/api/builders/${selectedBuilder.id}/invite-admin`;
      } else if (inviteType === 'company_admin') {
        url = `${API_URL}/api/sales-companies/${selectedCompany.id}/invite-admin`;
      } else if (inviteType === 'sales_rep') {
        url = `${API_URL}/api/sales-companies/${selectedCompany.id}/invite-sales-rep`;
      }
      
      const response = await axios.post(`${url}?email=${encodeURIComponent(inviteForm.email)}&name=${encodeURIComponent(inviteForm.name)}`, {}, { headers });
      
      setInviteResult(response.data);
      toast.success('User created successfully');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      platform_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      builder_admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      builder_staff: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
      sales_company_admin: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      sales_rep: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      customer: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="platform-admin-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Platform Administration</h1>
            <p className="text-gray-500 dark:text-gray-400">Manage builders, sales companies, and users across the platform</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100 dark:bg-gray-800">
            <TabsTrigger value="overview" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="builders" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Building2 className="w-4 h-4 mr-2" />
              Builders
            </TabsTrigger>
            <TabsTrigger value="companies" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Store className="w-4 h-4 mr-2" />
              Sales Companies
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Users className="w-4 h-4 mr-2" />
              All Users
            </TabsTrigger>
            <TabsTrigger value="backup" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700" data-testid="backup-tab-trigger">
              <DatabaseBackup className="w-4 h-4 mr-2" />
              Backup &amp; Restore
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Builders</p>
                      <p className="text-3xl font-bold">{dashboardStats?.stats?.builders || 0}</p>
                    </div>
                    <Building2 className="w-10 h-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Sales Companies</p>
                      <p className="text-3xl font-bold">{dashboardStats?.stats?.sales_companies || 0}</p>
                    </div>
                    <Store className="w-10 h-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Total Users</p>
                      <p className="text-3xl font-bold">{dashboardStats?.stats?.users || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">Total Leads</p>
                      <p className="text-3xl font-bold">{dashboardStats?.stats?.leads || 0}</p>
                    </div>
                    <FileText className="w-10 h-10 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Customers</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{dashboardStats?.stats?.customers || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Projects</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{dashboardStats?.stats?.projects || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Leads This Month</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {dashboardStats?.leads_trend?.[dashboardStats.leads_trend.length - 1]?.count || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Builders</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardStats?.recent_builders?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardStats.recent_builders.map((builder) => (
                        <div key={builder.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{builder.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(builder.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-4">No builders yet</p>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Sales Companies</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardStats?.recent_sales_companies?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardStats.recent_sales_companies.map((company) => (
                        <div key={company.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                              <Store className="w-5 h-5 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{company.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(company.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-4">No sales companies yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Builders Tab */}
          <TabsContent value="builders" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Builders ({builders.length})</h2>
              <Button onClick={() => setShowBuilderModal(true)} data-testid="add-builder-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Builder
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {builders.map((builder) => (
                <Card key={builder.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: builder.branding?.primary_color || '#C41E3A' }}
                        >
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{builder.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{builder.slug}</p>
                        </div>
                      </div>
                      <Badge variant={builder.is_active ? "default" : "secondary"}>
                        {builder.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Mail className="w-4 h-4" />
                        <span>{builder.email}</span>
                      </div>
                      {builder.phone && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <Phone className="w-4 h-4" />
                          <span>{builder.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Store className="w-4 h-4" />
                        <span>{builder.sales_companies_count || 0} Sales Companies</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Users className="w-4 h-4" />
                        <span>{builder.users_count || 0} Users</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4 pt-4 border-t dark:border-gray-700">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setSelectedBuilder(builder);
                          setInviteType('builder_admin');
                          setInviteForm({ email: '', name: '' });
                          setInviteResult(null);
                          setShowInviteModal(true);
                        }}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Invite Admin
                      </Button>
                      <Button size="sm" variant="outline">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {builders.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="p-12 text-center">
                    <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Builders Yet</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">Get started by adding your first builder</p>
                    <Button onClick={() => setShowBuilderModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Builder
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Sales Companies Tab */}
          <TabsContent value="companies" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Sales Companies ({salesCompanies.length})</h2>
              <Button onClick={() => setShowCompanyModal(true)} data-testid="add-company-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Sales Company
              </Button>
            </div>

            {/* Filter by builder */}
            <div className="flex items-center gap-4">
              <select
                className="px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                onChange={(e) => fetchSalesCompanies(e.target.value || null)}
              >
                <option value="">All Builders</option>
                {builders.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {salesCompanies.map((company) => (
                <Card key={company.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: company.branding?.primary_color || '#C41E3A' }}
                        >
                          <Store className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{company.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{company.subdomain}</p>
                        </div>
                      </div>
                      <Badge variant={company.is_active ? "default" : "secondary"}>
                        {company.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Building2 className="w-4 h-4" />
                        <span>{builders.find(b => b.id === company.builder_id)?.name || 'Unknown Builder'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Mail className="w-4 h-4" />
                        <span>{company.email}</span>
                      </div>
                      {company.phone && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <Phone className="w-4 h-4" />
                          <span>{company.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Users className="w-4 h-4" />
                        <span>{company.users_count || 0} Users</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <FileText className="w-4 h-4" />
                        <span>{company.leads_count || 0} Leads</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4 pt-4 border-t dark:border-gray-700">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setSelectedCompany(company);
                          setInviteType('company_admin');
                          setInviteForm({ email: '', name: '' });
                          setInviteResult(null);
                          setShowInviteModal(true);
                        }}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Invite Admin
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedCompany(company);
                          setInviteType('sales_rep');
                          setInviteForm({ email: '', name: '' });
                          setInviteResult(null);
                          setShowInviteModal(true);
                        }}
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline">
                        <Settings className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {salesCompanies.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="p-12 text-center">
                    <Store className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Sales Companies Yet</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      {builders.length === 0 
                        ? 'Create a builder first, then add sales companies'
                        : 'Add your first sales company'
                      }
                    </p>
                    <Button onClick={() => setShowCompanyModal(true)} disabled={builders.length === 0}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Sales Company
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">All Users ({allUsers.length})</h2>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <select
                className="px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                value={userFilter.builder_id}
                onChange={(e) => setUserFilter({ ...userFilter, builder_id: e.target.value })}
              >
                <option value="">All Builders</option>
                {builders.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              
              <select
                className="px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                value={userFilter.sales_company_id}
                onChange={(e) => setUserFilter({ ...userFilter, sales_company_id: e.target.value })}
              >
                <option value="">All Companies</option>
                {salesCompanies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              
              <select
                className="px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                value={userFilter.tenant_role}
                onChange={(e) => setUserFilter({ ...userFilter, tenant_role: e.target.value })}
              >
                <option value="">All Roles</option>
                <option value="platform_admin">Platform Admin</option>
                <option value="builder_admin">Builder Admin</option>
                <option value="builder_staff">Builder Staff</option>
                <option value="sales_company_admin">Sales Company Admin</option>
                <option value="sales_rep">Sales Rep</option>
                <option value="customer">Customer</option>
              </select>
            </div>

            {/* Users Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Builder</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sales Company</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700">
                      {allUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <Badge className={getRoleBadgeColor(user.tenant_role)}>
                              {user.tenant_role?.replace('_', ' ') || 'N/A'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                            {user.builder_name || '-'}
                          </td>
                          <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                            {user.sales_company_name || '-'}
                          </td>
                          <td className="px-6 py-4">
                            <Badge variant={user.is_active ? "default" : "secondary"}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backup & Restore Tab */}
          <TabsContent value="backup" className="space-y-6" data-testid="backup-restore-tab-content">
            <BackupRestorePanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Builder Modal */}
      <Dialog open={showBuilderModal} onOpenChange={setShowBuilderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Builder</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateBuilder} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Company Name *</label>
              <Input
                required
                value={builderForm.name}
                onChange={(e) => setBuilderForm({ ...builderForm, name: e.target.value })}
                placeholder="Acme Metal Buildings"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Slug (URL-friendly)</label>
              <Input
                value={builderForm.slug}
                onChange={(e) => setBuilderForm({ ...builderForm, slug: e.target.value })}
                placeholder="acme-metal (auto-generated if empty)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Email *</label>
              <Input
                type="email"
                required
                value={builderForm.email}
                onChange={(e) => setBuilderForm({ ...builderForm, email: e.target.value })}
                placeholder="admin@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Phone</label>
              <Input
                value={builderForm.phone}
                onChange={(e) => setBuilderForm({ ...builderForm, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Website</label>
              <Input
                value={builderForm.website}
                onChange={(e) => setBuilderForm({ ...builderForm, website: e.target.value })}
                placeholder="https://www.company.com"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBuilderModal(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Builder</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Sales Company Modal */}
      <Dialog open={showCompanyModal} onOpenChange={setShowCompanyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Sales Company</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCompany} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Builder *</label>
              <select
                required
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                value={companyForm.builder_id}
                onChange={(e) => setCompanyForm({ ...companyForm, builder_id: e.target.value })}
              >
                <option value="">Select Builder...</option>
                {builders.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Company Name *</label>
              <Input
                required
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                placeholder="Florida Metal Sales"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Slug (subdomain)</label>
              <Input
                value={companyForm.slug}
                onChange={(e) => setCompanyForm({ ...companyForm, slug: e.target.value })}
                placeholder="florida-metal (auto-generated if empty)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Email *</label>
              <Input
                type="email"
                required
                value={companyForm.email}
                onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                placeholder="sales@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Phone</label>
              <Input
                value={companyForm.phone}
                onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCompanyModal(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Company</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite User Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {inviteType === 'builder_admin' && `Invite Builder Admin - ${selectedBuilder?.name}`}
              {inviteType === 'company_admin' && `Invite Sales Company Admin - ${selectedCompany?.name}`}
              {inviteType === 'sales_rep' && `Invite Sales Rep - ${selectedCompany?.name}`}
            </DialogTitle>
          </DialogHeader>
          
          {inviteResult ? (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">✓ User Created!</h3>
                <p className="text-sm text-green-700 dark:text-green-300">{inviteResult.message}</p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">Email:</span>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{inviteResult.email}</code>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(inviteResult.email)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">Temp Password:</span>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 px-2 py-1 rounded font-mono">
                      {inviteResult.temp_password}
                    </code>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(inviteResult.temp_password)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-amber-600 dark:text-amber-400">
                ⚠️ Share these credentials securely with the user. They should change their password after first login.
              </p>
              
              <DialogFooter>
                <Button onClick={() => {
                  setShowInviteModal(false);
                  setInviteResult(null);
                }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleInviteUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Full Name *</label>
                <Input
                  required
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Email *</label>
                <Input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="john@company.com"
                />
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                <p className="text-blue-800 dark:text-blue-200">
                  A temporary password will be generated. Share it securely with the user.
                </p>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create User</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default PlatformAdminPage;
